'use client'
import { useRef, useState } from 'react'
import { IMAGE_CONTEXTS, type ImageContext } from '../lib/image-system'

interface ImageUploadProps {
  value: string
  onChange: (url: string) => void
  label?: string
  folder?: string
  /* §13 Images: one ratio per context — the dropzone and preview render AT the
     target ratio with cover-crop, so committees see exactly what the card will
     look like before saving. */
  context?: ImageContext
}

export default function ImageUpload({ value, onChange, label = 'Image', folder = 'masjids', context = 'campaign' }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [mode, setMode] = useState<'upload' | 'url'>('upload')
  const [urlInput, setUrlInput] = useState(value)
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const spec = IMAGE_CONTEXTS[context]

  async function handleFile(file: File) {
    setUploading(true)
    setError('')
    setLocalPreview(URL.createObjectURL(file)) // live crop preview at target ratio
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('folder', folder)
      form.append('context', context) // server normalizes to the context ratio
      const res = await fetch('/api/upload', { method: 'POST', body: form })
      const data = await res.json().catch(() => ({} as any))
      // Honesty: a demo/placeholder response (Cloudinary misconfigured → demo:true)
      // is a FAILED upload, not a real image — never surface it as saved.
      if (!res.ok || !data?.url || data?.demo) {
        setError(data?.error || (data?.demo ? 'Image storage isn’t enabled on the server.' : 'Upload failed'))
        return
      }
      onChange(data.url)
    } catch { setError('Upload failed. Please try again.') }
    finally { setUploading(false) }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const shown = localPreview || value
  const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={lbl}>{label} <span style={{ fontWeight: 400, color: '#999' }}>· crops to {spec.ratio.replace(/ /g, '')}</span></label>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['upload', 'url'] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} style={{ padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: mode === m ? '#0E7C61' : '#f5f5f3', color: mode === m ? 'white' : '#555' }}>
              {m === 'upload' ? '📤 Upload' : '🔗 URL'}
            </button>
          ))}
        </div>
      </div>

      {mode === 'upload' ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{ border: '2px dashed ' + (uploading ? '#0E7C61' : '#e8e8e8'), borderRadius: 12, cursor: 'pointer', overflow: 'hidden', position: 'relative', aspectRatio: spec.ratio, maxHeight: 280, width: '100%', background: '#f8f8f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}
        >
          {shown ? (
            <>
              <img src={shown} alt="Preview at card crop" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
              {uploading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 13 }}>
                  Uploading…
                </div>
              )}
              {!uploading && (
                <div style={{ position: 'absolute', left: 8, bottom: 8, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: 10.5, borderRadius: 99, padding: '3px 9px' }}>
                  Shown exactly as it will appear on the card
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: 32 }}>{uploading ? '⏳' : '📸'}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>{uploading ? 'Uploading…' : 'Click or drag to upload'}</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>JPG, PNG, WebP — max 5MB · auto-cropped to {spec.ratio.replace(/ /g, '')}</div>
            </>
          )}
          <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
        </div>
      ) : (
        <div>
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onBlur={() => urlInput && onChange(urlInput)}
            placeholder="https://example.com/image.jpg"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e8e8e8', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' as const, marginBottom: 8 }}
          />
          {urlInput && (
            <div style={{ aspectRatio: spec.ratio, maxHeight: 280, borderRadius: 10, overflow: 'hidden' }}>
              <img src={urlInput} alt="Preview at card crop" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setError('Invalid image URL')} />
            </div>
          )}
        </div>
      )}

      {error && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>⚠️ {error}</div>}
    </div>
  )
}
